import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

export type ApiErrorDto = {
  data: {
    error: ApiError;
  };
};

export type ApiError = {
  class: number;
  code: string;
  lineNumber: number;
  message: string;
  name: string;
  number: number;
  originalError: {
    info: {
      name: string;
      event: string;
      number: number;
      state: number;
      class: number;
    };
    precedingErrors: string[];
    procName: string;
    serverName: string;
    stack: string;
    state: number;
    statusCode: number;
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isApiErrorResponseDto(res: any): res is ApiErrorDto {
  return res && "data" in res && "error" in res.data && "message" in res.data.error;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isApiError(res: any): res is ApiError {
  return (
    res &&
    typeof res === "object" &&
    res !== null &&
    "class" in res &&
    "code" in res &&
    "lineNumber" in res &&
    "message" in res &&
    "name" in res &&
    "number" in res &&
    "originalError" in res
  );
}

function isCustomFetchError(
  ex: unknown
): ex is { status: "CUSTOM_ERROR"; error: string } {
  return (
    typeof ex === "object" &&
    ex !== null &&
    "status" in ex &&
    ex.status === "CUSTOM_ERROR" &&
    "error" in ex &&
    typeof ex.error === "string"
  );
}

export function asApiError(
  ex: unknown,
  whenOtherType?: string,
  prefix?: string
): string | undefined {
  if (isApiError(ex)) {
    const err = ex.message;
    return `${prefix || ""}${err}`;
  }
  if (isCustomFetchError(ex)) {
    try {
      const parsed: unknown = JSON.parse(ex.error);
      if (isApiError(parsed)) {
        return `${prefix || ""}${parsed.message}`;
      }
    } catch {
      return whenOtherType;
    }
  }
  return whenOtherType;
}

export const transformApiErrorResponse = (
  baseQueryReturnValue: unknown
): FetchBaseQueryError => {
  if (!isApiErrorResponseDto(baseQueryReturnValue)) {
    return {
      status: "CUSTOM_ERROR",
      error: `NOT_AN_API_ERROR: ${JSON.stringify(baseQueryReturnValue)}`,
    };
  }
  const apiError: ApiError = baseQueryReturnValue.data.error;
  if (!apiError) {
    return {
      status: "CUSTOM_ERROR",
      error: `ERROR_SHOULD_BE_A_JSON: ${JSON.stringify(baseQueryReturnValue)}`,
    };
  }
  return { status: "CUSTOM_ERROR", error: JSON.stringify(apiError) };
};
